import React from 'react'

import {
  AlignItems,
  Bold,
  Box,
  Display,
  JustifyContent,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'
import classnames from 'classnames'

import { DISCOUNT_BANNER_BREAKPOINT_PX, DiscountSavingsBanner } from '../DiscountSavingsBanner'
import type { AppliedDiscount } from '../DiscountSavingsBanner'

import css from './OrderConfirmationHeader.css'

// Only renders horizontally-inline as a pill here
const VisibleAboveDiscountBannerBreakpoint = withCitrusTheme(Box, () => ({
  [`@media (max-width: ${DISCOUNT_BANNER_BREAKPOINT_PX}px)`]: {
    display: 'none',
  },
}))

type Props = {
  canEditOrder: boolean
  deliveryDate: string
  deliveryStart: string
  deliveryEnd: string
  whenCutoffTime: string
  whenCutoffDate: string
  isTransactional?: boolean
  showDiscountSavingsBanner?: boolean
  discountPercentage?: string
  discountAmountSaved?: string
  appliedDiscount?: AppliedDiscount
}

const OrderConfirmationHeader = ({
  canEditOrder,
  deliveryDate,
  deliveryStart,
  deliveryEnd,
  whenCutoffTime,
  whenCutoffDate,
  isTransactional,
  showDiscountSavingsBanner,
  discountPercentage,
  discountAmountSaved,
  appliedDiscount,
}: Props) => {
  const appliedDiscountPill = appliedDiscount ? (
    <VisibleAboveDiscountBannerBreakpoint flexShrink={0}>
      <DiscountSavingsBanner
        variant="pill"
        title={appliedDiscount.title}
        subtitle={appliedDiscount.subtitle}
      />
    </VisibleAboveDiscountBannerBreakpoint>
  ) : null

  const darkDiscountPill =
    showDiscountSavingsBanner && discountPercentage && discountAmountSaved ? (
      <VisibleAboveDiscountBannerBreakpoint flexShrink={0}>
        <DiscountSavingsBanner
          variant="pill"
          tone="dark"
          title={`${discountPercentage} discount applied`}
          subtitle={`Saved ${discountAmountSaved}`}
        />
      </VisibleAboveDiscountBannerBreakpoint>
    ) : null

  return (
    <div data-testing="orderConfirmationHeader">
      {isTransactional ? (
        <>
          <Box
            display={Display.Flex}
            alignItems={AlignItems.Center}
            justifyContent={JustifyContent.SpaceBetween}
            gap={3}
            style={{ marginBottom: '1rem' }}
          >
            <p className={css.paragraph} style={{ paddingBottom: 0, minWidth: 0, flexShrink: 1 }}>
              This won&apos;t start or change your subscription.
            </p>
            {appliedDiscountPill ?? darkDiscountPill}
          </Box>
          <p data-testid="delivery-date-message" className={css.paragraph}>
            Delivery scheduled for{' '}
            <Bold>
              {deliveryDate}, {deliveryStart}-{deliveryEnd}
            </Bold>
            . Edit your choices by{' '}
            <Bold>
              {whenCutoffTime} {whenCutoffDate}
            </Bold>
            .
          </p>
        </>
      ) : (
        <>
          <Box
            display={Display.Flex}
            alignItems={AlignItems.Center}
            justifyContent={JustifyContent.SpaceBetween}
            gap={3}
            style={{ marginBottom: '1rem' }}
          >
            <p className={css.paragraph} style={{ paddingBottom: 0, minWidth: 0, flexShrink: 1 }}>
              Delivery date: {deliveryDate}
              {', '}between {deliveryStart} and {deliveryEnd}
            </p>
            {appliedDiscountPill}
          </Box>
          <p className={classnames(css.paragraph, css.bold)}>
            {canEditOrder
              ? `You can edit your choices until ${whenCutoffTime} on ${whenCutoffDate}`
              : `You can no longer edit your choices as editing closed at ${whenCutoffTime} on ${whenCutoffDate}`}
          </p>
        </>
      )}
    </div>
  )
}

export { OrderConfirmationHeader }
